import React from 'react';
import './ShopifyUploadPopup.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function ShopifyUploadPopup({
  showShopifyUploadPopup,
  uploadShopifyWeek, setUploadShopifyWeek,
  uploadShopifyMonth, setUploadShopifyMonth,
  uploadShopifyYear, setUploadShopifyYear,
  howManyWeeks, setHowManyWeeks,
  uploadMode, setUploadMode,
  shopifyPeriodMode, setShopifyPeriodMode,
  parsedShopifyData,
  uploadShopifyStatus,
  isLoadingPreview, isLoadingUpload,
  loadShopifyPreview,
  handleShopifyUploadSubmit,
  closeShopifyUploadPopup,
  setParsedShopifyData,
  setUploadShopifyStatus
}) {
  const isWeekly = shopifyPeriodMode === 'weekly';

  const handlePeriodModeChange = (mode) => {
    setShopifyPeriodMode(mode);
    setParsedShopifyData([]);
    setUploadShopifyStatus(null);
    setUploadShopifyWeek('');
    setUploadShopifyMonth('');
  };

  const isPreviewDisabled =
    !uploadShopifyYear ||
    (isWeekly ? (!uploadShopifyWeek || howManyWeeks < 1) : !uploadShopifyMonth) ||
    isLoadingPreview || isLoadingUpload;

  const isSubmitDisabled =
    !uploadShopifyYear ||
    (isWeekly ? (!uploadShopifyWeek || howManyWeeks < 1) : !uploadShopifyMonth) ||
    parsedShopifyData.length === 0 ||
    isLoadingUpload || isLoadingPreview;

  return (
    <>
      {showShopifyUploadPopup && (
        <div className="shopify-popup-overlay">
          <div className="shopify-popup-content">
            <div className="shopify-popup-header">
              <h2>Import Shopify Sales Data</h2>
              <button className="shopify-close-popup" onClick={closeShopifyUploadPopup}>✕</button>
            </div>

            <div className="shopify-upload-form">

              {/* ── Period mode toggle ── */}
              <div className="shopify-form-group">
                <label>Period Type</label>
                <div className="shopify-mode-buttons">
                  <button
                    className={`shopify-mode-btn ${isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('weekly')}
                  >
                    Weekly
                  </button>
                  <button
                    className={`shopify-mode-btn ${!isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('monthly')}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* ── API info ── */}
              <div className="shopify-file-info">
                <h3>Shopify API Import</h3>
                <ul>
                  <li><strong>Data source:</strong> Shopify Admin API</li>
                  <li><strong>Store:</strong> organo-republic.myshopify.com</li>
                  <li><strong>Data type:</strong> Paid orders only</li>
                  {isWeekly && <li><strong>Week definition:</strong> Monday to Sunday (ISO week)</li>}
                </ul>
              </div>

              {/* ── Week range mode (only for weekly) ── */}
              {isWeekly && (
                <div className="shopify-form-group">
                  <label>Import Mode</label>
                  <div className="shopify-mode-buttons">
                    <button
                      className={`shopify-mode-btn ${uploadMode === 'single' ? 'active' : ''}`}
                      onClick={() => setUploadMode('single')}
                    >
                      Single Week
                    </button>
                    <button
                      className={`shopify-mode-btn ${uploadMode === 'range' ? 'active' : ''}`}
                      onClick={() => setUploadMode('range')}
                    >
                      Multiple Weeks
                    </button>
                  </div>
                </div>
              )}

              {/* ── Period fields ── */}
              <div className="form-row">
                {isWeekly ? (
                  <div className="shopify-form-group half-width">
                    <label>Start Week Number *</label>
                    <input
                      type="number" min="1" max="53"
                      value={uploadShopifyWeek}
                      onChange={(e) => setUploadShopifyWeek(e.target.value)}
                      placeholder="Week (1-53)"
                    />
                  </div>
                ) : (
                  <div className="shopify-form-group half-width">
                    <label>Month *</label>
                    <select
                      value={uploadShopifyMonth}
                      onChange={(e) => setUploadShopifyMonth(e.target.value)}
                    >
                      <option value="">Select month</option>
                      {MONTHS.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="shopify-form-group half-width">
                  <label>Year *</label>
                  <input
                    type="number" min="2020" max="2030"
                    value={uploadShopifyYear}
                    onChange={(e) => setUploadShopifyYear(e.target.value)}
                    placeholder="Year"
                  />
                </div>
              </div>

              {/* ── Number of weeks (only for weekly range mode) ── */}
              {isWeekly && uploadMode === 'range' && (
                <div className="shopify-form-group">
                  <label>Number of Weeks to Import *</label>
                  <div className="shopify-weeks-selector">
                    <input
                      type="number" min="1" max="52"
                      value={howManyWeeks}
                      onChange={(e) => setHowManyWeeks(parseInt(e.target.value) || 1)}
                      placeholder="Number of weeks"
                      className="shopify-weeks-input"
                    />
                    <div className="shopify-quick-weeks">
                      {[4, 8, 12].map(n => (
                        <button key={n} className="shopify-quick-week-btn" onClick={() => setHowManyWeeks(n)}>
                          {n} weeks
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="shopify-file-hint">Number of consecutive weeks to import, starting from the specified week</p>
                </div>
              )}

              {/* ── Preview button ── */}
              <div className="shopify-preview-section">
                <button
                  className="shopify-preview-btn"
                  onClick={loadShopifyPreview}
                  disabled={isPreviewDisabled}
                >
                  {isLoadingPreview ? (
                    <><span className="loading-spinner"></span> Fetching from Shopify...</>
                  ) : (
                    'Preview Data from Shopify'
                  )}
                </button>
                <p className="shopify-preview-hint">Click to fetch data from Shopify API before uploading to database</p>
              </div>

              {/* ── Status ── */}
              {uploadShopifyStatus && (
                <div className={`shopify-upload-status ${uploadShopifyStatus.type}`}>
                  {uploadShopifyStatus.type === 'loading' && <span className="loading-spinner"></span>}
                  {uploadShopifyStatus.message}
                  {uploadShopifyStatus.details && (
                    <div className="shopify-status-details">{uploadShopifyStatus.details}</div>
                  )}
                </div>
              )}

              {/* ── Preview table ── */}
              {parsedShopifyData.length > 0 && (
                <div className="shopify-data-preview">
                  <div className="shopify-preview-header">
                    <h3>Shopify Data Preview ({parsedShopifyData.length} records)</h3>
                    <div className="shopify-preview-stats">
                      <span className="shopify-stat-item">
                        Unique SKUs: {new Set(parsedShopifyData.map(r => r.sku)).size}
                      </span>
                      <span className="shopify-stat-item">
                        Total Quantity: {parsedShopifyData.reduce((s, r) => s + (r.quantity || 0), 0).toLocaleString()}
                      </span>
                      <span className="shopify-stat-item">
                        {isWeekly ? `Weeks: ${howManyWeeks}` : `Month: ${MONTHS[uploadShopifyMonth - 1] || uploadShopifyMonth}`}
                      </span>
                    </div>
                  </div>
                  <div className="shopify-preview-table-container">
                    <table className="shopify-preview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>SKU</th>
                          {isWeekly ? <th>Week</th> : <th>Month</th>}
                          <th>Year</th>
                          <th>Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedShopifyData.slice(0, 10).map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className="shopify-sku-cell">{row.sku || 'N/A'}</td>
                            <td className="shopify-number-cell">
                              {isWeekly ? `W${row.weeknumber}` : (MONTHS[row.month - 1] || row.month)}
                            </td>
                            <td className="shopify-number-cell">{row.year}</td>
                            <td className="shopify-number-cell">
                              <span className={row.quantity > 0 ? 'shopify-positive-value' : 'shopify-zero-value'}>
                                {row.quantity || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedShopifyData.length > 10 && (
                      <p className="shopify-preview-more">⋮ ... and {parsedShopifyData.length - 10} more records</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Buttons ── */}
              <div className="shopify-popup-buttons">
                <button className="shopify-cancel-btn" onClick={closeShopifyUploadPopup}>Cancel</button>
                <button
                  className="shopify-submit-btn"
                  onClick={handleShopifyUploadSubmit}
                  disabled={isSubmitDisabled}
                >
                  {isLoadingUpload ? (
                    <><span className="loading-spinner"></span> Uploading...</>
                  ) : (
                    'Upload to Database'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ShopifyUploadPopup;